/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
import {
  FETCH_ACTION,
  UPLOAD_INIT_EXTRACTION_REQUEST,
  UPLOAD_INIT_EXTRACTION_RESPONSE,
  UPLOAD_COMPLETE_EXTRACTION_REQUEST,
  UPLOAD_COMPLETE_EXTRACTION_RESPONSE,
  CREATE_EXTRACTION_JOB_REQUEST,
  CREATE_EXTRACTION_JOB_RESPONSE,
  GET_EXTRACTION_JOB_REQUEST,
  GET_EXTRACTION_JOB_RESPONSE,
  GET_EXTRACTION_JOB_FOR_MODEL_REQUEST,
  GET_EXTRACTION_JOB_FOR_MODEL_RESPONSE,
  GET_EXTRACTION_DOCUMENT_REQUEST,
  GET_EXTRACTION_DOCUMENT_RESPONSE,
} from '#constants/actionTypes.js';

/*
 * The extraction job's own endpoints (data & API design §2.2) -- upload init/complete,
 * create (start extraction), get (poll), getForModel (cold-reload discovery). These ride
 * the same FETCH_ACTION middleware every other HTTP call in the app uses (see putModel/
 * fetchModel, actions/models/model.js), so they behave like ordinary redux-thunk-returned
 * promises: dispatch(...).then(({ payload }) => ...).
 */

export const initExtractionUpload = (modelId, body) => ({
  [FETCH_ACTION]: {
    types: [UPLOAD_INIT_EXTRACTION_REQUEST, UPLOAD_INIT_EXTRACTION_RESPONSE],
    endpoint: `/model/${modelId}/extraction/upload`,
    options: { method: 'POST', body },
    meta: { modelId },
  },
});

export const completeExtractionUpload = (modelId, body) => ({
  [FETCH_ACTION]: {
    types: [UPLOAD_COMPLETE_EXTRACTION_REQUEST, UPLOAD_COMPLETE_EXTRACTION_RESPONSE],
    endpoint: `/model/${modelId}/extraction/upload/complete`,
    options: { method: 'POST', body },
    meta: { modelId },
  },
});

export const createExtractionJob = (modelId, body) => ({
  [FETCH_ACTION]: {
    types: [CREATE_EXTRACTION_JOB_REQUEST, CREATE_EXTRACTION_JOB_RESPONSE],
    endpoint: `/model/${modelId}/extraction`,
    options: { method: 'POST', body },
    meta: { modelId },
  },
});

export const getExtractionJob = (modelId, jobId) => ({
  [FETCH_ACTION]: {
    types: [GET_EXTRACTION_JOB_REQUEST, GET_EXTRACTION_JOB_RESPONSE],
    endpoint: `/model/${modelId}/extraction/${jobId}`,
    meta: { modelId, jobId },
  },
});

export const getExtractionJobForModel = modelId => ({
  [FETCH_ACTION]: {
    types: [GET_EXTRACTION_JOB_FOR_MODEL_REQUEST, GET_EXTRACTION_JOB_FOR_MODEL_RESPONSE],
    endpoint: `/model/${modelId}/extraction`,
    meta: { modelId },
  },
});

/*
 * The `document` op (UI design §3.3) -- unlike every other extraction endpoint, the real
 * route takes `documentS3Key` as a query parameter, not a JSON body, so it's built into
 * the endpoint string here rather than passed as an `options.body`.
 */
export const getExtractionDocument = (modelId, documentS3Key) => ({
  [FETCH_ACTION]: {
    types: [GET_EXTRACTION_DOCUMENT_REQUEST, GET_EXTRACTION_DOCUMENT_RESPONSE],
    endpoint: `/model/${modelId}/extraction/document?documentS3Key=${encodeURIComponent(
      documentS3Key
    )}`,
    meta: { modelId },
  },
});

/*
 * Resolves the jobId the progress view should poll (UI design §2.2): if the caller already
 * has one (the happy path -- it just created the job, or it survived in-memory from an
 * earlier render), that's used as-is with no extra call. If not (a browser refresh lost
 * it), rehydrates it via `getForModel` (core design §4.11's cold-reload discovery) --
 * resolving to `null` if the model has never had a job (e.g. the user Skipped upload).
 * This does NOT make the mandatory `get` call itself (UI design §2.2 -- that call is never
 * skippable regardless of how the jobId was resolved); it only resolves which jobId to poll.
 */
export const rehydrateExtractionJob = (modelId, knownJobId) => dispatch => {
  if (knownJobId) {
    return Promise.resolve(knownJobId);
  }
  return dispatch(getExtractionJobForModel(modelId)).then(
    ({ payload }) => (payload.job && payload.job.jobId) || null
  );
};

// The presigned S3 PUT itself isn't app API traffic -- no auth header, no JSON envelope, and
// it targets S3 (or, in dev, the HTTP stub's fake S3 origin), not the app's API base URL -- so
// it bypasses the FETCH_ACTION middleware and calls fetch directly, same as a browser would.
const putFileToPresignedUrl = (uploadUrl, file) =>
  fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'application/pdf' } });

/*
 * The upload -> create chain (data & API design §2.1/§2.2): presigned-S3 init, a direct PUT of the
 * file bytes, then create (starts the extraction job). v1 only exercises the single presigned-PUT
 * branch of `uploadInitResponse` (`uploadUrl`), not the multipart `parts` branch -- there's no
 * reason for this UI to chunk a single PDF upload (the multipart threshold is 100 MiB).
 *
 * A single-PUT upload takes NO `complete` step: the PUT itself finalizes the object, and `create`'s
 * HeadObject confirms it exists. `upload/complete` is multipart-only -- it runs S3
 * CompleteMultipartUpload, so calling it after a single PUT rejects with `NoSuchUpload` (there is no
 * multipart upload to complete), which broke every extraction upload. If the multipart branch is
 * ever wired up, its per-part PUTs are what `completeExtractionUpload` would finalize.
 */
export const uploadDocumentAndStartExtraction = (modelId, file) => dispatch =>
  dispatch(
    initExtractionUpload(modelId, {
      fileName: file.name,
      mediaType: file.type,
      fileSize: file.size,
    })
  )
    .then(({ payload: initPayload }) =>
      putFileToPresignedUrl(initPayload.uploadUrl, file).then(() => initPayload)
    )
    .then(initPayload =>
      dispatch(createExtractionJob(modelId, { documentS3Key: initPayload.documentS3Key }))
    )
    .then(({ payload }) => payload);
